import { useState, useEffect } from "react";
import { Download, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { downloadEdition, removeEdition, isSaved } from "@/lib/offline";

// "Télécharger" everywhere now means: save the revue into the offline library
// (no PDF file download). Shows progress and a saved/check state.
export const SaveOfflineButton = ({ edition, className = "" }) => {
  const [saved, setSaved] = useState(() => isSaved(edition.id));
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState(0);

  useEffect(() => {
    const sync = () => setSaved(isSaved(edition.id));
    window.addEventListener("appresse:offline-changed", sync);
    return () => window.removeEventListener("appresse:offline-changed", sync);
  }, [edition.id]);

  const run = async (e) => {
    e?.stopPropagation?.(); e?.preventDefault?.();
    if (busy) return;
    if (saved) {
      await removeEdition(edition.id);
      setSaved(false);
      toast.success("Revue retirée du hors-ligne");
      return;
    }
    setBusy(true); setProg(0);
    try {
      await downloadEdition(edition, (n, total) => setProg(Math.round((n / Math.max(1, total)) * 100)));
      setSaved(true);
      toast.success("Revue enregistrée ! Retrouvez-la dans l'onglet « Hors-ligne » (icône téléchargement, en bas de l'écran).", { duration: 6000 });
    } catch (err) {
      toast.error(err?.message || "Échec de l'enregistrement hors-ligne");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      size="icon"
      variant="outline"
      className={`h-8 w-8 shrink-0 relative ${saved ? "border-emerald-500/60 text-emerald-600 hover:text-emerald-700" : ""} ${className}`}
      onClick={run}
      disabled={busy}
      data-testid={`save-offline-button-${edition.id}`}
      aria-label={saved ? "Retirer du hors-ligne" : "Enregistrer hors-ligne"}
      title={saved ? "Enregistrée hors-ligne — retrouvez-la dans l'onglet « Hors-ligne » (cliquer pour retirer)" : "Enregistrer pour lire hors connexion — à retrouver dans l'onglet « Hors-ligne »"}
    >
      {busy
        ? <span className="text-[9px] font-bold leading-none">{prog}%</span>
        : saved
          ? <CheckCircle2 className="w-4 h-4" />
          : <Download className="w-4 h-4" />}
    </Button>
  );
};
